import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sparkles, CheckCircle2, AlertTriangle, Loader2, ArrowRight,
  Boxes, Wrench, Workflow, ShieldCheck, CalendarDays, Scale,
  Users, MapPin,
} from "lucide-react";

// Setup Progress Dashboard. Single page that answers the
// only two questions a new admin actually has: what have I done, what's
// left before automation can switch on?

const TILE_META = {
  categories:  { label: "Assignment categories", Icon: Boxes,      route: "/app/categories",       help: "Types of work your employees can be assigned to." },
  skills:      { label: "Employee skills",       Icon: Wrench,     route: "/app/skills",           help: "Skills your employees can have. Add each employee's own skills later on their profile." },
  assignment:  { label: "Assignment Rules", Icon: Workflow,   route: "/app/rules",            help: "Who can be assigned to which work." },
  coverage:    { label: "Coverage",         Icon: ShieldCheck, route: "/app/policies",        help: "Minimum people on each slot." },
  leave:       { label: "Leave",            Icon: CalendarDays, route: "/app/policies",      help: "Time-off rules and limits." },
  fairness:    { label: "Fairness",         Icon: Scale,      route: "/app/policies",         help: "Spread unpopular work evenly." },
  employees:   { label: "Employees",        Icon: Users,      route: "/app/employees",        help: "Your team and their preferences." },
  locations:   { label: "Locations",        Icon: MapPin,     route: "/app/locations",        help: "Where your team works from." },
};

export default function SetupHubPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/automation/setup-progress");
      setData(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load Setup progress");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="py-10 text-center text-sm text-[#4B5773]">
      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading setup…
    </div>;
  }
  if (!data) return null;

  const total = data.tiles.length;
  const done = data.tiles.filter((t) => t.status === "done").length;
  const pct = Math.round((done / total) * 100);

  return (
    <div data-testid="setup-hub-page">
      <div className="mb-6">
        <div className="eyebrow">Onboarding</div>
        <h1 className="font-display text-4xl mt-1">Setup</h1>
        <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
          Track what you&apos;ve finished and what NextRota still needs before you
          can switch automation on. Sensible defaults are available wherever
          possible, so you should not need to build anything from scratch.
        </p>
      </div>

      <div className="rounded-lg border border-[#BFD3FB] bg-[#F0F6FF] p-4 mb-6"
           data-testid="setup-progress-summary">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-medium text-[#0B1A36]">
              {data.ready_for_l3
                ? "You're ready to enable Draft Automation"
                : `${data.steps_remaining_for_l3} step${data.steps_remaining_for_l3 === 1 ? "" : "s"} remaining before Draft Automation`}
            </div>
            <p className="text-xs text-[#1F2A44] mt-1">
              {done} of {total} setup areas complete. Each tile below shows the
              single next action for that area.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-48 h-2 rounded-full bg-white border border-[#BFD3FB] overflow-hidden">
              <div className="h-full bg-[#2563EB]" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-medium text-[#1D4ED8]">{pct}%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
           data-testid="setup-tiles">
        {data.tiles.map((t) => (
          <SetupTile key={t.page} tile={t} onChanged={load} navigate={navigate} />
        ))}
      </div>
    </div>
  );
}

function SetupTile({ tile, onChanged, navigate }) {
  const meta = TILE_META[tile.page] || { label: tile.page, Icon: Boxes, route: "/app", help: "" };
  const Icon = meta.Icon;
  const [busy, setBusy] = useState(false);
  const isDone = tile.status === "done";
  const isEmpty = tile.status === "empty";
  const statusColour = isDone
    ? "border-emerald-200 bg-emerald-50"
    : isEmpty ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50";
  const StatusIcon = isDone ? CheckCircle2 : AlertTriangle;
  const statusIconColour = isDone ? "text-emerald-600" : (isEmpty ? "text-rose-600" : "text-amber-600");

  async function generate() {
    if (!tile.fix_hint) return;
    setBusy(true);
    try {
      const status = await api.get("/setup/wizard-status").catch(() => ({ data: {} }));
      const templateId = status.data?.recommended_template_id || "office_small";
      const { data } = await api.post("/setup/wizard-complete", {
        template_id: templateId,
        industry: status.data?.industry,
        size_band: status.data?.size_band,
        only_kinds: tile.fix_hint.only_kinds,
        dry_run: false,
      });
      const created = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Created ${created} item${created === 1 ? "" : "s"}. Existing items were kept untouched.`);
      onChanged();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not generate");
    } finally { setBusy(false); }
  }

  return (
    <div className={`rounded-lg border ${statusColour} p-4 flex flex-col gap-2`}
         data-testid={`setup-tile-${tile.page}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#0B1A36]" />
        <h3 className="font-medium text-sm text-[#0B1A36]">{meta.label}</h3>
        <StatusIcon className={`h-4 w-4 ml-auto ${statusIconColour}`} />
      </div>
      <div className="text-2xl font-medium text-[#0B1A36]">{tile.count}</div>
      <p className="text-[11px] text-[#4B5773] leading-snug">{meta.help}</p>
      {tile.top_blocker && (
        <div className="text-[11px] text-amber-900 bg-amber-100/60 border border-amber-200 rounded px-2 py-1"
             data-testid={`setup-tile-${tile.page}-blocker`}>
          <strong>Next:</strong> {tile.top_blocker.label}
          {tile.top_blocker.blocks_level && (
            <span className="ml-1 text-amber-800">(blocks {tile.top_blocker.blocks_level})</span>
          )}
        </div>
      )}
      <div className="mt-1 flex items-center gap-1.5">
        {tile.fix_hint && !isDone && (
          <Button size="sm" onClick={generate} disabled={busy}
                  className="h-7 text-xs bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                  data-testid={`setup-tile-${tile.page}-fix`}>
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
            {tile.fix_hint.label}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => navigate(meta.route)}
                className="h-7 text-xs"
                data-testid={`setup-tile-${tile.page}-open`}>
          Open <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}
